import { createContext, useContext } from 'react';

import { createStore, StoreApi, useStore } from 'zustand';

import { GetReviewsQuery } from '@notino/shared/definitions/types';

export type ReviewFilterCategory =
  | 'rating'
  | 'contentTypes'
  | 'variants'
  | 'shops';
export type SelectionValue = number | string;

interface State {
  currentCategory: ReviewFilterCategory | null;
  selections: Record<ReviewFilterCategory, SelectionValue[]>;
  temporarySelections: SelectionValue[];
  filtersData?: NonNullable<GetReviewsQuery['reviewsAndFilters']>['filter'];
}

interface ReviewFiltersState extends State {
  actions: {
    setFilters: (
      filters?: NonNullable<GetReviewsQuery['reviewsAndFilters']>['filter']
    ) => void;
    openCategory: (category: ReviewFilterCategory) => void;
    toggleSelection: (value: SelectionValue) => void;
    apply: () => void;
    clearCategory: (category: ReviewFilterCategory) => void;
    selectSingleRating: (value: SelectionValue) => void;
  };
}

type ReviewFiltersStore = StoreApi<ReviewFiltersState>;

const initialState: State = {
  currentCategory: null,
  selections: {
    rating: [],
    contentTypes: [],
    variants: [],
    shops: [],
  },
  temporarySelections: [],
};

export const createReviewFiltersStore = (
  initState?: Partial<Omit<State, 'selections'>> & {
    selections?: Partial<State['selections']>;
  }
) =>
  createStore<ReviewFiltersState>((set, get) => ({
    ...initialState,
    ...initState,
    selections: {
      ...initialState.selections,
      ...initState?.selections,
    },
    actions: {
      setFilters: (filters) => set({ filtersData: filters }),
      openCategory: (category) =>
        set((state) => ({
          currentCategory: category,
          temporarySelections: [...state.selections[category]],
        })),
      toggleSelection: (value) =>
        set((state) => {
          if (!state.currentCategory) {
            return state;
          }

          const exists = state.temporarySelections.includes(value);

          const next = exists
            ? state.temporarySelections.filter((v) => v !== value)
            : [...state.temporarySelections, value];

          return { temporarySelections: next };
        }),
      selectSingleRating: (value) =>
        set((state) => ({
          selections: { ...state.selections, rating: [value] },
          temporarySelections:
            state.currentCategory === 'rating'
              ? [value]
              : state.temporarySelections,
        })),
      apply: () => {
        const { currentCategory, temporarySelections, selections } = get();

        if (!currentCategory) {
          return;
        }

        set({
          selections: {
            ...selections,
            [currentCategory]: [...temporarySelections],
          },
          temporarySelections: [],
          currentCategory: null,
        });
      },
      clearCategory: (category) => {
        set((state) => ({
          selections: { ...state.selections, [category]: [] },
          temporarySelections:
            state.currentCategory === category ? [] : state.temporarySelections,
        }));
      },
    },
  }));

const Context = createContext<{ store: ReviewFiltersStore }>(null!);
export const ReviewFiltersContextProvider = Context.Provider;

export const useReviewFiltersStore = <T>(
  selector: Parameters<typeof useStore<ReviewFiltersStore, T>>[1]
) => {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error(
      'useReviewFiltersStore must be used inside ReviewFiltersContextProvider'
    );
  }
  return useStore(ctx.store, selector);
};

export const useReviewFilterActions = () =>
  useReviewFiltersStore((s) => s.actions);
